import { useState, useEffect } from 'react';
import { Camera, User, Mail, Phone, Building, Lock, Globe, Moon, Sun, Save, Shield, Bell } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        company_name: user.company_name || ''
      });
    }
  }, [user]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    toast.info('Photo upload feature coming soon!');
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User, color: '#3B82F6' },
    { id: 'appearance', label: 'Appearance', icon: isDark ? Moon : Sun, color: '#8B5CF6' },
    { id: 'notifications', label: 'Notifications', icon: Bell, color: '#F97316' },
    { id: 'security', label: 'Security', icon: Shield, color: '#10B981' }
  ];

  return (
    <DashboardLayout currentPage="/dashboard/settings">
      <div className="max-w-6xl">
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-8">Settings</h1>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Tabs Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    data-testid={`tab-${tab.id}`}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-[#3B82F6]/20 to-[#8B5CF6]/10 text-[var(--text-primary)]'
                        : 'text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <Icon size={20} style={{ color: activeTab === tab.id ? tab.color : 'inherit' }} />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-6 md:p-8">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Profile Settings</h2>

                  {/* Profile Photo */}
                  <div className="flex items-center gap-6 mb-8 pb-8 border-b border-[var(--border)]">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}>
                        {user?.profile_photo ? (
                          <img src={user.profile_photo} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          <User size={36} className="text-white" />
                        )}
                      </div>
                      <label className="absolute -bottom-2 -right-2 bg-[#F97316] p-2 rounded-lg cursor-pointer hover:bg-[#FB923C] transition-all shadow-lg">
                        <Camera size={14} className="text-white" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <div>
                      <h3 className="text-[var(--text-primary)] font-semibold mb-1">{user?.name}</h3>
                      <p className="text-[var(--text-muted)] text-sm mb-1">{user?.email}</p>
                      <p className="text-[#3B82F6] text-sm font-medium capitalize">{user?.user_type} Account</p>
                    </div>
                  </div>

                  {/* Profile Form */}
                  <div className="space-y-5">
                    <div>
                      <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                        <User size={16} className="text-[#3B82F6]" />
                        Full Name
                      </label>
                      <input
                        type="text"
                        data-testid="settings-name"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/50 transition-all"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                        <Mail size={16} className="text-[#8B5CF6]" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        disabled
                        className="w-full bg-[var(--bg-dark)]/50 border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-muted)] cursor-not-allowed"
                      />
                      <p className="text-[var(--text-muted)] text-xs mt-1">Email cannot be changed</p>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                        <Phone size={16} className="text-[#10B981]" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        data-testid="settings-phone"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/50 transition-all"
                        placeholder="Enter your phone number"
                      />
                    </div>

                    {user?.user_type === 'business' && (
                      <div>
                        <label className="flex items-center gap-2 text-[var(--text-secondary)] mb-2 text-sm font-medium">
                          <Building size={16} className="text-[#F97316]" />
                          Company Name
                        </label>
                        <input
                          type="text"
                          data-testid="settings-company"
                          value={profileData.company_name}
                          onChange={(e) => setProfileData({ ...profileData, company_name: e.target.value })}
                          className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316]/50 transition-all"
                        />
                      </div>
                    )}

                    <button
                      onClick={handleSaveProfile}
                      disabled={loading}
                      className="px-8 py-3 font-semibold text-white rounded-xl transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#3B82F6]/25 flex items-center gap-2 disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                    >
                      <Save size={18} />
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}

              {/* Appearance Tab */}
              {activeTab === 'appearance' && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Appearance</h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: isDark ? '#8B5CF6' + '20' : '#F97316' + '20' }}>
                          {isDark ? (
                            <Moon size={24} className="text-[#8B5CF6]" />
                          ) : (
                            <Sun size={24} className="text-[#F97316]" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-[var(--text-primary)] font-semibold mb-1">
                            {isDark ? 'Dark Mode' : 'Light Mode'}
                          </h3>
                          <p className="text-[var(--text-muted)] text-sm">
                            {isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                          </p>
                        </div>
                      </div>
                      <button
                        data-testid="theme-toggle"
                        onClick={() => {
                          toggleTheme();
                          toast.success(`Switched to ${isDark ? 'light' : 'dark'} mode`);
                        }}
                        className={`relative w-16 h-8 rounded-full transition-all duration-300 ${
                          isDark ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6]' : 'bg-gradient-to-r from-[#F97316] to-[#FB923C]'
                        }`}
                      >
                        <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all duration-300 shadow-lg flex items-center justify-center ${
                          isDark ? 'left-9' : 'left-1'
                        }`}>
                          {isDark ? (
                            <Moon size={12} className="text-[#8B5CF6]" />
                          ) : (
                            <Sun size={12} className="text-[#F97316]" />
                          )}
                        </div>
                      </button>
                    </div>

                    {/* Theme Preview */}
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isDark ? 'border-[#3B82F6] ring-2 ring-[#3B82F6]/30' : 'border-[var(--border)] hover:border-[var(--border-hover)]'
                        }`}
                        style={{ background: '#0A0A0F' }}
                        onClick={() => !isDark && toggleTheme()}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Moon size={16} className="text-[#8B5CF6]" />
                          <span className="text-white text-sm font-medium">Dark</span>
                        </div>
                        <div className="space-y-1">
                          <div className="h-2 w-3/4 bg-white/20 rounded"></div>
                          <div className="h-2 w-1/2 bg-white/10 rounded"></div>
                        </div>
                      </div>
                      
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          !isDark ? 'border-[#F97316] ring-2 ring-[#F97316]/30' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={{ background: '#F8FAFC' }}
                        onClick={() => isDark && toggleTheme()}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Sun size={16} className="text-[#F97316]" />
                          <span className="text-gray-900 text-sm font-medium">Light</span>
                        </div>
                        <div className="space-y-1">
                          <div className="h-2 w-3/4 bg-gray-300 rounded"></div>
                          <div className="h-2 w-1/2 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Notification Preferences</h2>
                  <div className="space-y-4">
                    {[
                      { label: 'Email notifications', desc: 'Receive updates via email', enabled: true, color: '#3B82F6' },
                      { label: 'Project updates', desc: 'Get notified when project status changes', enabled: true, color: '#8B5CF6' },
                      { label: 'Marketing emails', desc: 'Receive promotional content', enabled: false, color: '#F97316' }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl">
                        <div>
                          <h3 className="text-[var(--text-primary)] font-semibold mb-1">{item.label}</h3>
                          <p className="text-[var(--text-muted)] text-sm">{item.desc}</p>
                        </div>
                        <button
                          onClick={() => toast.info('Notification settings coming soon!')}
                          className={`relative w-14 h-8 rounded-full transition-colors ${
                            item.enabled ? '' : 'bg-[var(--text-muted)]/20'
                          }`}
                          style={item.enabled ? { background: `linear-gradient(135deg, ${item.color}, ${item.color}99)` } : {}}
                        >
                          <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-lg ${
                            item.enabled ? 'left-7' : 'left-1'
                          }`}></div>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--text-primary)] mb-6">Security Settings</h2>
                  <div className="space-y-5">
                    <h3 className="text-[var(--text-primary)] font-semibold">Change Password</h3>
                    <div className="space-y-4">
                      <input
                        type="password"
                        placeholder="Current Password"
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/50 transition-all"
                      />
                      <input
                        type="password"
                        placeholder="New Password"
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/50 transition-all"
                      />
                      <input
                        type="password"
                        placeholder="Confirm New Password"
                        className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/50 transition-all"
                      />
                      <button
                        onClick={() => toast.info('Password change feature coming soon!')}
                        className="px-8 py-3 font-semibold text-white rounded-xl transition-all hover:scale-105"
                        style={{ background: 'linear-gradient(135deg, #10B981, #34D399)' }}
                      >
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
